import clsx from "clsx"
import { forwardRef, useRef, useState } from "react"
import { Controller, type FieldValues } from "react-hook-form"
import { Upload04 } from "@/shared/components/ui/icons"
import { tError } from "@/shared/config/i18n"
import type { CompoundedComponent, IFormControl } from "@/shared/types"

interface IProps {
  label?: string
  onUpload?: (file: File) => void
  accept?: string
  required?: boolean
  error?: string
  className?: string
}

const FileDropZone = forwardRef<HTMLInputElement, IProps>(
  ({ label, onUpload, accept = ".doc,.docx,.xls,.xlsx,.pdf", required = false, error, className }: IProps, _) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const [fileName, setFileName] = useState<string | null>(null)
    const [isDragOver, setIsDragOver] = useState(false)

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) {
        setFileName(file.name)
        onUpload?.(file)
      }
    }

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      setIsDragOver(false)
      const file = event.dataTransfer.files?.[0]
      if (file) {
        setFileName(file.name)
        onUpload?.(file)
      }
    }

    const triggerFileUpload = () => {
      fileInputRef.current?.click()
    }

    return (
      <div className={clsx("flex flex-col gap-2", className)}>
        {label && (
          <span className="flex h-[9px] items-center text-[13px]">
            {label}
            {(error || required) && <strong className="ml-0.5 text-[10px] text-red-500">＊</strong>}
          </span>
        )}
        <div
          className={clsx(
            "flex max-h-[42px] flex-1 cursor-pointer items-center gap-2 rounded-lg border border-border border-dashed p-4",
            isDragOver ? "border-blue-400 bg-gray-100" : "bg-[#F7F7F9]"
          )}
          onClick={triggerFileUpload}
          onDragOver={e => {
            e.preventDefault()
            setIsDragOver(true)
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
        >
          <span className="h-[17px] select-none text-DarkGray text-sm">
            {fileName || `Загрузить ${accept.replace(/\./g, "").replace(/,/g, ", ")}`}
          </span>
          {!fileName && <Upload04 size={18} className="text-DarkGray" />}
          <input ref={fileInputRef} type="file" accept={accept} className="hidden" onChange={handleFileChange} />
        </div>
        {error && <span className="text-[10px] text-red-500">{error}</span>}
      </div>
    )
  }
) as CompoundedComponent<IProps, HTMLInputElement, { Form: typeof FormFileDropZone }>

const FormFileDropZone = <T extends FieldValues>({ control, name, rules, required, label, onUpload, ...props }: IFormControl<T> & IProps) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? tError("required_field") : undefined,
        ...rules
      }}
      render={({ field, fieldState }) => {
        const handleFileChange = (file: File) => {
          field.onChange(file)
          onUpload?.(file)
        }

        return <FileDropZone {...field} onUpload={handleFileChange} error={fieldState.error?.message} label={label} required={required} {...props} />
      }}
    />
  )
}

FileDropZone.Form = FormFileDropZone
export default FileDropZone
